import type { CSSProperties, ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { useSkeletonAppearance } from '../../hooks/useSkeletonAppearance';

/**
 * Placeholders that take the final content's dimensions, so nothing moves when
 * the data lands. Pair with `useRevealGroup` (`hooks/useRevealGroup.ts`); the
 * pattern is written up in `apps/web/bill/src/components/ui/CLAUDE.md` § "Loading".
 */

interface SkeletonProps {
  /** Size and spacing only (`h-4 w-32`). `cn` does not merge conflicting Tailwind classes. */
  className?: string;
  /** Sits inside a line of text (a figure, a count) instead of as a block. */
  inline?: boolean;
  style?: CSSProperties;
}

/**
 * One grey bar. Decorative: the surrounding `SkeletonRegion` carries the announcement.
 * The block form is a `div` with no display class, so a caller's `hidden sm:block` applies cleanly.
 */
export function Skeleton({ className, inline = false, style }: SkeletonProps) {
  const barClass = cn('rounded bg-bg-muted motion-safe:animate-pulse', className);
  return inline
    ? <span aria-hidden="true" className={cn('inline-block align-middle', barClass)} style={style} />
    : <div aria-hidden="true" className={barClass} style={style} />;
}

interface SkeletonRegionProps {
  /** Read by screen readers in place of the bars, e.g. "Loading clients". */
  label: string;
  className?: string;
  children: ReactNode;
}

/**
 * Wraps a set of skeletons as one polite loading announcement. It holds its
 * space but stays invisible for a moment, so a fast read replaces it unseen
 * (`useSkeletonAppearance`).
 */
export function SkeletonRegion({ label, className, children }: SkeletonRegionProps) {
  const appearance = useSkeletonAppearance<HTMLDivElement>();
  return (
    <div
      ref={appearance.ref}
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={cn(appearance.className, className)}
      style={appearance.style}
    >
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/** A column in `SkeletonRows`: its bar width, and the breakpoint below which the real column hides. */
export interface SkeletonColumn {
  width: string;
  hideBelow?: 'sm' | 'md' | 'lg';
  /** The real cell stacks a second line under its value below `sm` (an email under a name). */
  stackedBelowSm?: boolean;
}

const HIDE_BELOW_CLASS: Record<NonNullable<SkeletonColumn['hideBelow']>, string> = {
  sm: 'hidden sm:block',
  md: 'hidden md:block',
  lg: 'hidden lg:block',
};

const DEFAULT_COLUMNS: readonly SkeletonColumn[] = [
  { width: 'flex-1' },
  { width: 'w-32', hideBelow: 'sm' },
  { width: 'w-24', hideBelow: 'md' },
];

interface SkeletonRowsProps {
  /** Announces the rows on their own. Omit when they sit inside a larger `SkeletonRegion`. */
  label?: string;
  rows?: number;
  columns?: readonly SkeletonColumn[];
  /** Reserve the table's column-header band (`thead`). */
  header?: boolean;
  /**
   * Row padding, sized so a row matches the standard data-table row (a `text-sm`
   * cell beside a `Badge`, about 53px). Override only for a table whose rows differ.
   */
  rowClassName?: string;
  className?: string;
}

function columnClass(column: SkeletonColumn, height: string): string {
  return cn(height, column.width, column.hideBelow && HIDE_BELOW_CLASS[column.hideBelow]);
}

/** Table or list rows: one bar per column, columns hiding at the same breakpoints as the real table. */
export function SkeletonRows({
  label,
  rows = 5,
  columns = DEFAULT_COLUMNS,
  header = false,
  rowClassName = 'px-3 py-[18px] sm:px-4',
  className,
}: SkeletonRowsProps) {
  const body = (
    <>
      {header && (
        <div className="flex items-center gap-3 bg-bg-subtle px-3 py-3.5 sm:px-4">
          {Array.from(columns, (column, col) => (
            <div key={col} className={cn(column.width, column.hideBelow && HIDE_BELOW_CLASS[column.hideBelow])}>
              <Skeleton className="h-3 w-14" />
            </div>
          ))}
        </div>
      )}
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className={cn('flex items-center gap-3', rowClassName)}>
          {Array.from(columns, (column, col) => {
            // Staggering the pulse down the rows reads as one surface loading, not a grid blinking.
            const style = col === 0 ? { animationDelay: `${row * 60}ms` } : undefined;
            return column.stackedBelowSm ? (
              <div key={col} className={cn(column.width, column.hideBelow && HIDE_BELOW_CLASS[column.hideBelow])}>
                <Skeleton className="h-4 w-full" style={style} />
                <Skeleton className="mt-1.5 h-3 w-2/3 sm:hidden" />
              </div>
            ) : (
              <Skeleton key={col} className={columnClass(column, 'h-4')} style={style} />
            );
          })}
        </div>
      ))}
    </>
  );
  const rowsClass = cn('divide-y divide-border', className);
  return label
    ? <SkeletonRegion label={label} className={rowsClass}>{body}</SkeletonRegion>
    : <div aria-hidden="true" className={rowsClass}>{body}</div>;
}

/**
 * The standard list filter bar: a search field with a labelled `Select` beside
 * it, stacking below `sm`. Matches the clients and matters filter bars.
 */
export function SkeletonFilterBar({ className }: { className?: string }) {
  return (
    <div aria-hidden="true" className={cn('flex flex-col gap-3 sm:flex-row sm:items-end', className)}>
      <Skeleton className="h-10 w-full rounded-lg sm:flex-1" />
      <div className="sm:w-28">
        <Skeleton className="mb-2 h-4 w-12" />
        <Skeleton className="h-10 w-full rounded-lg" />
      </div>
    </div>
  );
}

interface SkeletonTableCardProps extends Omit<SkeletonRowsProps, 'label'> {
  label: string;
  /** Reserve the filter bar that sits above the real table. */
  toolbar?: boolean;
}

/** The standard list page: a `Card` holding an optional filter bar and a bordered table. */
export function SkeletonTableCard({ label, toolbar = false, className, ...rows }: SkeletonTableCardProps) {
  return (
    <SkeletonRegion
      label={label}
      className={cn('rounded-xl border border-border bg-bg-primary p-4 shadow-sm sm:p-6', className)}
    >
      {toolbar && <SkeletonFilterBar className="mb-4" />}
      <div className="overflow-hidden rounded-lg border border-border">
        <SkeletonRows {...rows} />
      </div>
    </SkeletonRegion>
  );
}

interface SkeletonLinesProps {
  label: string;
  lines?: number;
  className?: string;
}

/** Short stacked lines: a small section, a settings card body, a history list. */
export function SkeletonLines({ label, lines = 3, className }: SkeletonLinesProps) {
  return (
    <SkeletonRegion label={label} className={cn('space-y-3', className)}>
      {Array.from({ length: lines }, (_, i) => (
        // The last line is shorter so the block reads as text, not a table.
        <Skeleton key={i} className={cn('h-4', i === lines - 1 && lines > 1 ? 'w-2/3' : 'w-full')} />
      ))}
    </SkeletonRegion>
  );
}

interface SkeletonCardProps {
  label: string;
  lines?: number;
  className?: string;
}

/** A `Card` with a heading bar and body lines. */
export function SkeletonCard({ label, lines = 3, className }: SkeletonCardProps) {
  return (
    <div className={cn('rounded-xl border border-border bg-bg-primary p-4 shadow-sm sm:p-6', className)}>
      <Skeleton className="mb-4 h-5 w-40" />
      <SkeletonLines label={label} lines={lines} />
    </div>
  );
}

interface SkeletonDetailPageProps {
  label: string;
  /** Body cards below the header card. */
  cards?: number;
}

/** A single-record page: a `PageHeaderCard` (title, meta line) then body cards. */
export function SkeletonDetailPage({ label, cards = 1 }: SkeletonDetailPageProps) {
  return (
    <SkeletonRegion label={label} className="space-y-4 sm:space-y-6">
      <div className="rounded-xl border border-border bg-bg-primary p-4 shadow-sm sm:p-6">
        <Skeleton className="h-7 w-56 sm:h-8" />
        <Skeleton className="mt-3 h-4 w-72 max-w-full" />
      </div>
      {Array.from({ length: cards }, (_, i) => (
        <div key={i} className="rounded-xl border border-border bg-bg-primary p-4 shadow-sm sm:p-6">
          <Skeleton className="mb-4 h-5 w-40" />
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        </div>
      ))}
    </SkeletonRegion>
  );
}
